// Library Methods
import i18next from "i18next";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "src/components/shared/alerts/Alert/Alert";
import IconButton from "@mui/material/IconButton";
import { PlusIcon, SearchIcon } from "src/assets/icons";

import navbarImg from "src/assets/images/operating-manual/instructions-for-use/navbar.png";
import navbarFrImg from "src/assets/images/operating-manual/instructions-for-use/navbar-fr.png";
import examCardImg from "src/assets/images/operating-manual/instructions-for-use/exam-card.png";
import examCardFrImg from "src/assets/images/operating-manual/instructions-for-use/exam-card-fr.png";
import addPatientImg from "src/assets/images/operating-manual/instructions-for-use/add-patient.png";
import addPatientFrImg from "src/assets/images/operating-manual/instructions-for-use/add-patient-fr.png";

// Config
import { priorityLanguageMap } from "src/config/languages";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";

const CreateSelectPatient = () => {
  const { t } = useTranslation("operating-manual");

  const currentLang = i18next.language as keyof typeof priorityLanguageMap;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Typography>
        1.{" "}
        {t(
          "Navigate to the Patients page. Click 'Patients' on the navigation bar. It should be underlined."
        )}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <img
          src={currentLang === "fr" ? navbarFrImg : navbarImg}
          width="75%"
          alt="navbar"
        />
      </Box>

      <Typography>
        2. {t("Add a patient using the ")}
        <IconButton
          sx={{
            width: `${UI_SIZES.iconButtonSize}px`,
            height: `${UI_SIZES.iconButtonSize}px`,
            color: "#FFF",
            backgroundColor: "#202338",
            borderRadius: "4px",
            pointerEvents: "none",
          }}
        >
          <PlusIcon />
        </IconButton>
        {t(
          " Add button. Select the patient by clicking anywhere within the patient bar. Once selected, the bar will be highlighted in dark blue."
        )}
      </Typography>
      <Typography>
        {t("You can also ")}
        <IconButton
          sx={{
            width: `${UI_SIZES.iconButtonSize}px`,
            height: `${UI_SIZES.iconButtonSize}px`,
            color: "#FFF",
            backgroundColor: "#202338",
            borderRadius: "4px",
            pointerEvents: "none",
          }}
        >
          <SearchIcon />
        </IconButton>
        {t(" search for an existing patient by name or unique ID.")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <img
          src={currentLang === "fr" ? examCardFrImg : examCardImg}
          width="75%"
          alt="exam-card"
        />
      </Box>

      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              mt: "20px",
              gap: "20px",
            }}
          >
            <Typography>
              3. {t("Fill in the following patient fields and click add")}:
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Typography fontWeight={600}>
                {t("First and Last Name")}
              </Typography>
              <Typography>
                {t("Enter the patient's full legal name.")}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Typography fontWeight={600}>
                {t("Healthcare ID (Optional)")}
              </Typography>
              <Typography>
                {t(
                  "If your clinic uses a unique identifier for patient records, enter it here. This field allows for quicker searches and record retrieval."
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Typography fontWeight={600}>{t("Birthdate")}</Typography>
              <Typography>
                {t(
                  "Record the patient's date of birth in the format (DD/MM/YYYY)."
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Typography fontWeight={600}>{t("Sex")}</Typography>
              <Typography>
                {t(
                  "Select the patient's sex as recorded in their medical file."
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Typography fontWeight={600}>{t("Language")}</Typography>
              <Typography>
                {t(
                  "Select the patient's preferred language for the exam and on-screen instructions."
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Typography fontWeight={600}>
                {t("OD and OS Prescription (Optional)")}
              </Typography>
              <Typography>
                {t(
                  "If available, enter the patient's right eye (OD) and left eye (OS) prescription details. This is optional but may be useful for reference."
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Typography fontWeight={600}>{t("Exam Captions")}</Typography>
              <Typography>
                {t(
                  "Enabling this option will display on-screen subtitles during the examination. This feature is recommended for patients who are hard of hearing."
                )}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
            }}
          >
            <img
              src={currentLang === "fr" ? addPatientFrImg : addPatientImg}
              width="100%"
              alt="add-patient"
            />
          </Box>
        </Grid>
      </Grid>

      <Alert
        variant="info"
        alertContent={t(
          "Note: The language you select when adding a patient will be the language of the voice prompts."
        )}
      />
    </Box>
  );
};

export default CreateSelectPatient;
